import { useEffect, useRef, useState } from 'react';

/**
 * Reusable hook for scroll-triggered entrance animations.
 * Returns a ref to attach to the container element, a visibility boolean,
 * and a helper function for staggered child styles.
 *
 * @param {Object}  options
 * @param {number}  options.threshold  - IO threshold (0–1), default 0.12
 * @param {boolean} options.triggerOnce - only trigger once, default true
 * @returns {{ ref, isVisible, revealStyle, stagger }}
 */
export function useScrollReveal(options = {}) {
  const { threshold = 0.12, triggerOnce = true } = options;
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          if (triggerOnce) observer.unobserve(element);
        }
      },
      { threshold }
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, [threshold, triggerOnce]);

  /** Base reveal style for the section header / single element */
  const revealStyle = (delay = 0) => ({
    opacity: isVisible ? 1 : 0,
    transform: isVisible ? 'translateY(0px)' : 'translateY(24px)',
    transition: `opacity 0.7s cubic-bezier(0.4, 0, 0.2, 1) ${delay}ms, transform 0.7s cubic-bezier(0.4, 0, 0.2, 1) ${delay}ms`,
  });

  /** Staggered reveal for a list of cards — pass the index */
  const stagger = (index, baseDelay = 100, perItem = 120) => ({
    opacity: isVisible ? 1 : 0,
    transform: isVisible ? 'translateY(0px)' : 'translateY(28px)',
    transition: `opacity 0.6s cubic-bezier(0.4, 0, 0.2, 1) ${baseDelay + index * perItem}ms, transform 0.6s cubic-bezier(0.4, 0, 0.2, 1) ${baseDelay + index * perItem}ms`,
  });

  return { ref, isVisible, revealStyle, stagger };
}
